import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import Image from 'next/image';
import { snakeCase } from 'lodash-es';
import { v4 as uuid } from 'uuid';

import { Box, useDisclosure } from '@chakra-ui/react';

import { FormField } from '~/cms/components/form-input/form-input-model';
import { Card } from '~/components/card';
import { TemplateForm, translateFormDataToFormFields } from '~/components/template-form';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { useUser } from '~/features/user/hooks/use-user';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

import { EnterpriseStartTime } from '../models/enterprise-webinar-content';
import { LeadCaptureType } from '../models/shared-models';

import { EnterpriseResourceRegistrationModal } from './enterprise-resource-register-modal';

export type EnterpriseResourceFormSegmentProperties = { [key: string]: any } & {
  [PREFERRED_TIME_NAME: string]: string;
};

export type EnterpriseResourceFormProps = {
  resourceType: 'ebook' | 'infographic' | 'report' | 'webinar' | 'video' | 'live-event';
  templateForm: TemplateFormType;
  leadCaptureType: LeadCaptureType | null;
  startTimes?: EnterpriseStartTime[];
  startDate?: string | null;
  fileUrl?: string;
  additionalEventProperties?: { [key: string]: any };
  enableResponseModal?: boolean;
  successModalProps?: { title: string; message: string };
  errorModalProps?: { title: string; message: string };
};

const PREFERRED_TIME_NAME = 'preferredTime';

export function EnterpriseResourceFormComponent(
  {
    resourceType,
    templateForm,
    leadCaptureType,
    startTimes = [],
    startDate,
    fileUrl,
    additionalEventProperties,
    enableResponseModal,
    successModalProps,
    errorModalProps,
  }: EnterpriseResourceFormProps,
  ref?: ForwardedRef<HTMLDivElement>
) {
  const { user } = useUser();
  const { isOpen: isSuccessModalOpen, onOpen: openSuccessModal, onClose: closeSuccessModal } = useDisclosure();
  const { isOpen: isErrorModalOpen, onOpen: openErrorModal, onClose: closeErrorModal } = useDisclosure();
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
  const [segmentProperties, setSegmentProperties] = useState<EnterpriseResourceFormSegmentProperties>();
  const { sendSegmentLeadFormEvents, isSubmitting } = useLeadFormEvent();
  const hasOnlyOneStartTime = startTimes.length === 1;
  const hasCustomUrl = Boolean(
    templateForm?.submitButton?.buttonAction === 'Custom URL' && templateForm?.submitButton?.customUrl
  );

  const additionalFields: FormField[] = useMemo(() => {
    if (hasOnlyOneStartTime || startTimes.length === 0) {
      return [];
    }

    const formFields = translateFormDataToFormFields(templateForm.formData);
    const formFieldsLength = formFields.length;
    const radioOptions = startTimes.map((startTime) => ({
      label: `${startTime.region}: ${startTime.time}`,
      value: startTime.time,
    }));

    return [
      {
        inputType: 'radio',
        options: radioOptions,
        id: uuid(),
        order: formFieldsLength + 1,
        name: PREFERRED_TIME_NAME,
        label: 'Select preferred time:',
        required: true,
      },
    ];
  }, [startTimes, templateForm.formData, hasOnlyOneStartTime]);

  async function handleSubmit({ formData }: { templateForm: TemplateFormType; formData: any }) {
    const properties: EnterpriseResourceFormSegmentProperties = {
      ...transformFormDataToEventProperties({
        ...formData,
        ...(hasOnlyOneStartTime && { [PREFERRED_TIME_NAME]: startTimes[0].time }), // Add start time automatically if only 1 exists
      }),
      ...(!formData.workEmail && formData.email && { work_email: formData.email }), // Note: work_email is necessary for the event to be sent to Marketo correctly as work_email is used as a primary key.
    };
    const sendToSalesforce = templateForm.sendToSalesforce ?? undefined;
    const identifyLabel = leadCaptureType === 'B2C' ? 'Consumer Lead' : `BD Interested - Enterprise Lead - HQ`;
    const eventName = leadCaptureType === 'B2C' ? 'Marketing Qualified Lead Form Submitted' : 'Enterprise Lead';

    setHasSubmittedForm(true);
    setSegmentProperties(properties);

    const segmentResponse = await sendSegmentLeadFormEvents({
      identifyTraits: {
        ...properties,
        // Note: the label here is necessary for the event to be sent to Marketo.
        // See: https://app.segment.com/udacity/destinations/marketo-v2/sources/frontend-endor/instances/5f5017123c6d5f1f65e281ce/filters
        label: identifyLabel,
      },
      trackAnalyticsEvent: {
        name: eventName,
        type: 'submit',
        label: resourceType,
        action: 'form_submitted',
        additionalAttributes: { ...properties, ...additionalEventProperties },
      },
      integrations: { sendToSalesforce },
    });

    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }

    // TODO - Remove if this doesn't fix the Marketo issue
    if (templateForm?.submitButton?.buttonAction === 'Custom URL' && templateForm?.submitButton?.customUrl) {
      window.open(templateForm.submitButton.customUrl, '_blank');
    }

    if (segmentResponse.success) {
      openSuccessModal();
    } else {
      openErrorModal();
      setHasSubmittedForm(false);
    }
  }

  const email = user?.email ?? '';

  return (
    <Box ref={ref} position='relative'>
      <Card borderRadius='4px 24px 4px 4px' padding='40px' paddingBottom='56px' position='relative' zIndex='2'>
        <Box>
          <TemplateForm
            additionalFormFields={additionalFields}
            isDisabled={hasSubmittedForm}
            isFullWidth={true}
            preventCustomUrlNavigation={true}
            templateForm={templateForm}
            defaultValues={{
              email,
            }}
            submitButtonProps={{
              style: { width: '100%' },
              isLoading: isSubmitting,
            }}
            onSubmit={handleSubmit}
          />
        </Box>
      </Card>
      <Box
        height='100%'
        left='0px'
        marginLeft={{ base: '-16px', md: '-120px', lg: '-120px' }}
        marginTop={{ base: '-24px', md: '-48px', lg: '-64px' }}
        pointerEvents='none'
        position='absolute'
        top='0px'
        width={{ base: '100vw', lg: '720px' }}
        zIndex='1'
      >
        <Image
          alt=''
          src='/images/backgrounds/floating-shapes-3.png'
          style={{
            objectFit: 'contain',
            objectPosition: 'top center',
          }}
          fill
          priority
        />
      </Box>

      {!hasCustomUrl && successModalProps && enableResponseModal && (
        <EnterpriseResourceRegistrationModal
          date={startDate}
          isOpen={isSuccessModalOpen}
          message={successModalProps.message}
          state='success'
          time={segmentProperties?.[snakeCase(PREFERRED_TIME_NAME)]}
          title={successModalProps.title}
          onClose={closeSuccessModal}
        />
      )}

      {!hasCustomUrl && errorModalProps && enableResponseModal && (
        <EnterpriseResourceRegistrationModal
          isOpen={isErrorModalOpen}
          message={errorModalProps.message}
          state='error'
          title={errorModalProps.title}
          onClose={closeErrorModal}
        />
      )}
    </Box>
  );
}

export const EnterpriseResourceForm = forwardRef(EnterpriseResourceFormComponent);
